import React, { useEffect, useState } from 'react';
import { Form, Select, Button, Card, Input, App, Spin, Alert } from 'antd';
import { SaveOutlined, LockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../hooks/useLanguage';
import api from '../services/api';
import { authService } from '../services/auth';

interface UserSettings {
  language: string;
  currency: string;
}

const Settings: React.FC = () => {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const { message } = App.useApp();
  const { changeLanguage } = useLanguage();
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const user = authService.getCurrentUser();

  const currencies = [
    { value: 'USD', label: t('settings:currencies.USD') },
    { value: 'EUR', label: t('settings:currencies.EUR') },
    { value: 'PLN', label: t('settings:currencies.PLN') },
  ];

  const languages = [
    { value: 'en', label: t('settings:languages.en') },
    { value: 'pl', label: t('settings:languages.pl') },
  ];

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      form.setFieldsValue({
        language: response.data.user?.language || i18n.language,
        currency: response.data.user?.preferredCurrency || 'EUR',
      });
    } catch (error) {
      // If settings don't exist yet, use defaults
      form.setFieldsValue({
        language: i18n.language,
        currency: 'EUR',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (values: UserSettings) => {
    setSaving(true);
    try {
      await api.put('/settings', {
        language: values.language,
        preferredCurrency: values.currency,
      });
      
      // Update the language immediately if it changed
      if (values.language !== i18n.language) {
        await changeLanguage(values.language);
      }

      message.success(t('settings:messages.saveSuccess'));
    } catch (error) {
      message.error(t('settings:messages.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (values: { currentPassword: string; newPassword: string; confirmPassword: string }) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error(t('settings:messages.passwordMismatch'));
      return;
    }
    setChangingPassword(true);
    try {
      await api.put('/users/me/password', {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      message.success(t('settings:messages.passwordChanged'));
      passwordForm.resetFields();
    } catch (error: any) {
      if (error.response?.data?.error === 'INVALID_PASSWORD') {
        message.error(t('settings:messages.currentPasswordWrong'));
      } else {
        message.error(error.response?.data?.message || t('settings:messages.passwordError'));
      }
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  return (
    <div>
      <h2>{t('settings:title')}</h2>
      
      <Card title={t('settings:userPreferences')} style={{ maxWidth: 600, marginTop: 24 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveSettings}
          onFinishFailed={() => message.warning(t('settings:messages.fillRequired'))}
          scrollToFirstError
        >
          <Form.Item
            name="language"
            label={t('settings:language')}
            rules={[{ required: true, message: t('settings:validation.languageRequired') }]}
          >
            <Select options={languages} />
          </Form.Item>

          <Form.Item
            name="currency"
            label={t('settings:currency')}
            rules={[{ required: true, message: t('settings:validation.currencyRequired') }]}
          >
            <Select options={currencies} />
          </Form.Item>

          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={saving}
              icon={<SaveOutlined />}
            >
              {t('common:save')}
            </Button>
          </Form.Item>
        </Form>

        {user && (
          <Alert
            message={t('settings:currentUser')}
            description={`${user.name} (${t(`common:userRole.${user.role}`)})`}
            type="info"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}
      </Card>

      <Card title={t('settings:changePassword')} style={{ maxWidth: 600, marginTop: 24 }}>
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleChangePassword}
          onFinishFailed={() => message.warning(t('settings:messages.fillRequired'))}
          scrollToFirstError
        >
          <Form.Item
            name="currentPassword"
            label={t('settings:currentPassword')}
            rules={[{ required: true, message: t('settings:validation.currentPasswordRequired') }]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label={t('settings:newPassword')}
            rules={[
              { required: true, message: t('settings:validation.newPasswordRequired') },
              { min: 6, message: t('settings:validation.newPasswordMin') },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label={t('settings:confirmPassword')}
            rules={[
              { required: true, message: t('settings:validation.confirmPasswordRequired') },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={changingPassword}
              icon={<LockOutlined />}
            >
              {t('settings:changePassword')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Settings;